import { LANGUAGES, type Language } from '@lyrmo/shared/options';

/** The interface speaks every language a rewrite can be written in. */
export type Locale = Exclude<Language, 'auto'>;

export const LOCALES: readonly Locale[] = LANGUAGES.filter(
  (language): language is Locale => language !== 'auto',
);

/** Each language in its own words, as the language picker lists them. */
export const LOCALE_NAMES: Readonly<Record<Locale, string>> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português',
  nl: 'Nederlands',
  pl: 'Polski',
  uk: 'Українська',
  cs: 'Čeština',
  sv: 'Svenska',
  da: 'Dansk',
  no: 'Norsk',
  fi: 'Suomi',
  ro: 'Română',
  el: 'Ελληνικά',
  tr: 'Türkçe',
  ar: 'العربية',
  he: 'עברית',
  hi: 'हिन्दी',
  id: 'Bahasa Indonesia',
  vi: 'Tiếng Việt',
  th: 'ไทย',
  ja: '日本語',
  ko: '한국어',
  zh: '简体中文',
};

const RIGHT_TO_LEFT: ReadonlySet<Locale> = new Set(['ar', 'he']);

export const directionOf = (locale: Locale): 'ltr' | 'rtl' =>
  RIGHT_TO_LEFT.has(locale) ? 'rtl' : 'ltr';

/** The tag for Intl and the page's lang attribute: Chinese is written in Simplified script. */
export const localeTag = (locale: Locale): string => (locale === 'zh' ? 'zh-Hans' : locale);

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value);

// Older or regional tags for the same languages.
const ALIASES: Readonly<Record<string, Locale>> = { nb: 'no', nn: 'no', iw: 'he', in: 'id' };

/** The first supported language among the browser's preferences, or English. */
export function matchLocale(preferences: readonly string[]): Locale {
  for (const tag of preferences) {
    const [language = ''] = tag.toLowerCase().split('-', 1);
    const locale = ALIASES[language] ?? language;
    if (isLocale(locale)) return locale;
  }
  return 'en';
}

const STORAGE_KEY = 'lyrmo.locale';

/** The language chosen in this browser before, or else the browser's own. */
export function preferredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Storage can be unavailable, as in private windows.
  }
  return matchLocale(navigator.languages);
}

export function rememberLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Remembering is a convenience; the language still applies to this visit.
  }
}
